import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Typography, AppBar, Toolbar, Button, useMediaQuery, useTheme } from '@mui/material';
import { Logout, Dashboard, People } from '@mui/icons-material';
import Logo from '../common/Logo';
import { logout } from '../../services/authService';
import { toast } from 'react-toastify';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={2}
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
        }}
      >
        <Toolbar>
          <Box
            sx={{
              mr: 2,
              display: { xs: 'none', sm: 'block' },
              backgroundColor: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
            }}
          >
            <Logo size="small" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
              Alexa Travel Agency
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Payment Management
            </Typography>
          </Box>
          
          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <Button
              color="inherit"
              onClick={() => navigate('/admin')}
              startIcon={!isMobile && <Dashboard />}
              variant={location.pathname === '/admin' ? 'outlined' : 'text'}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: location.pathname === '/admin' ? 'rgba(255,255,255,0.5)' : undefined,
              }}
            >
              {isMobile ? <Dashboard /> : 'Dashboard'}
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/customers')}
              startIcon={!isMobile && <People />}
              variant={location.pathname === '/customers' ? 'outlined' : 'text'}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: location.pathname === '/customers' ? 'rgba(255,255,255,0.5)' : undefined,
              }}
            >
              {isMobile ? <People /> : 'Customers'}
            </Button>
          </Box>

          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={!isMobile && <Logout />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {isMobile ? <Logout /> : 'Logout'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {children}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: '#f5f5f5',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} Alexa Travel Agency. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminLayout;
